// Floating "Give Feedback" button available on every page.
//
// Flow: click the button → we screenshot the current view (before the panel
// opens, so the panel isn't in the shot) → optionally "point at" an element on
// the page so the note is anchored to a specific thing → write a note → send.
//
// Submissions land in dbo.Feedback via /api/data/feedback. Anyone signed in can
// submit; only admins can read them (Admin → Feedback).

import React, { useState, useRef, useEffect, useCallback } from "react";
import html2canvas from "html2canvas";
import Icon from "./Icon";
import { useData } from "../data/DataProvider";
import { createFeedback } from "../services/sqlApi";
import "../styles.css";

const CATEGORIES = ["Bug", "Idea", "Question"];

// Build a human label for whatever the user clicked, e.g.
// 'button "Show past months" — in Runoff Matrix'.
function describeElement(el) {
  if (!el) return "";
  const tag = el.tagName.toLowerCase();
  const own = (el.innerText || el.textContent || "").trim().replace(/\s+/g, " ").slice(0, 80);
  const kind =
    tag === "button" ? "button" :
    tag === "a" ? "link" :
    tag === "input" || tag === "select" || tag === "textarea" ? "field" :
    tag === "th" || tag === "td" ? "table cell" : tag;
  // Nearest card/section title gives useful context.
  let context = "";
  let node = el;
  for (let i = 0; i < 8 && node; i++) {
    node = node.parentElement;
    if (!node) break;
    const t = node.querySelector?.(".card-title, .section-title, .page-title");
    if (t && t.innerText) { context = t.innerText.trim().replace(/\s+/g, " ").slice(0, 60); break; }
  }
  return `${kind}${own ? ` "${own}"` : ""}${context ? ` — in ${context}` : ""}`;
}

export default function FeedbackWidget() {
  const data = useData();
  const [open, setOpen] = useState(false);
  const [picking, setPicking] = useState(false);
  const [shot, setShot] = useState(null);
  const [target, setTarget] = useState("");
  const [category, setCategory] = useState("Idea");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const highlightRef = useRef(null);

  const capture = useCallback(async () => {
    try {
      const canvas = await html2canvas(document.body, {
        scale: 0.6,
        useCORS: true,
        logging: false,
        windowWidth: document.documentElement.clientWidth,
        windowHeight: document.documentElement.clientHeight,
        x: window.scrollX,
        y: window.scrollY,
        width: document.documentElement.clientWidth,
        height: document.documentElement.clientHeight,
      });
      setShot(canvas.toDataURL("image/jpeg", 0.6)); // JPEG keeps it a few hundred KB
    } catch {
      setShot(null); // a failed screenshot shouldn't block the note
    }
  }, []);

  const start = async () => {
    setMsg(""); setNote(""); setTarget(""); setCategory("Idea");
    await capture();
    setOpen(true);
  };

  // ── element picker ──
  useEffect(() => {
    if (!picking) return;
    const box = document.createElement("div");
    Object.assign(box.style, {
      position: "fixed", pointerEvents: "none", zIndex: 2147483646,
      border: "2px solid #dfab01", background: "rgba(223,171,1,0.14)",
      borderRadius: "4px", transition: "all .05s linear",
    });
    document.body.appendChild(box);
    highlightRef.current = box;

    const move = (e) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (!el) return;
      const r = el.getBoundingClientRect();
      Object.assign(box.style, { left: r.left + "px", top: r.top + "px", width: r.width + "px", height: r.height + "px" });
    };
    const click = (e) => {
      e.preventDefault(); e.stopPropagation();
      const el = document.elementFromPoint(e.clientX, e.clientY);
      setTarget(describeElement(el));
      setPicking(false);
      setOpen(true);
    };
    const key = (e) => { if (e.key === "Escape") { setPicking(false); setOpen(true); } };

    document.addEventListener("mousemove", move, true);
    document.addEventListener("click", click, true);
    document.addEventListener("keydown", key, true);
    return () => {
      document.removeEventListener("mousemove", move, true);
      document.removeEventListener("click", click, true);
      document.removeEventListener("keydown", key, true);
      box.remove();
    };
  }, [picking]);

  const send = async () => {
    if (!note.trim()) { setMsg("Add a short note first."); return; }
    setBusy(true); setMsg("Sending…");
    try {
      const token = await data.getToken();
      await createFeedback(token, {
        Category: category,
        Note: note.trim(),
        PageUrl: window.location.pathname + window.location.search,
        ElementLabel: target,
        Screenshot: shot,
      });
      setMsg("✓ Thanks — sent.");
      setTimeout(() => { setOpen(false); setMsg(""); }, 1200);
    } catch (e) {
      setMsg("✗ " + (e.message || "Couldn't send."));
    } finally { setBusy(false); }
  };

  if (picking) {
    return (
      <div style={{ position: "fixed", top: 12, left: "50%", transform: "translateX(-50%)", zIndex: 2147483647,
        background: "var(--navy)", color: "#fff", padding: "8px 16px", borderRadius: 999, fontSize: 13, fontWeight: 700,
        boxShadow: "0 4px 14px rgba(0,0,0,.25)" }}>
        Click the thing you want to talk about · Esc to cancel
      </div>
    );
  }

  return (
    <>
      {!open && (
        <button className="btn btn-primary" onClick={start}
          style={{ position: "fixed", right: 20, bottom: 20, zIndex: 900, borderRadius: 999, padding: "10px 18px",
            boxShadow: "0 6px 18px rgba(33,52,105,.28)" }}>
          <Icon name="messages" size={15} style={{ verticalAlign: "middle", marginRight: 7 }} />
          Give Feedback
        </button>
      )}

      {open && (
        <div className="card" style={{ position: "fixed", right: 20, bottom: 20, zIndex: 901, width: 380, maxWidth: "calc(100vw - 40px)",
          maxHeight: "82vh", overflowY: "auto", boxShadow: "0 12px 40px rgba(23,35,71,.28)" }}>
          <div className="card-header">
            <div className="card-title">Give Feedback</div>
            <button className="btn btn-ghost" style={{ padding: "2px 9px", fontSize: 16, lineHeight: 1 }} onClick={() => setOpen(false)}>×</button>
          </div>

          <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
            {CATEGORIES.map(c => (
              <button key={c} className={`btn ${category === c ? "btn-primary" : "btn-ghost"}`}
                style={{ fontSize: 12, padding: "4px 12px" }} onClick={() => setCategory(c)}>{c}</button>
            ))}
          </div>

          <textarea className="form-input" rows={4} placeholder="What's wrong, or what would you like it to do?"
            value={note} onChange={e => setNote(e.target.value)} style={{ width: "100%", resize: "vertical", marginBottom: 10 }} />

          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
            <button className="btn btn-secondary" style={{ fontSize: 12, padding: "5px 12px" }}
              onClick={() => { setOpen(false); setPicking(true); }}>
              <Icon name="target" size={12} style={{ verticalAlign: "middle", marginRight: 5 }} />
              {target ? "Point at something else" : "Point at something"}
            </button>
            {target && <span style={{ fontSize: 11, color: "var(--gold-text)", fontWeight: 700 }}>{target}</span>}
          </div>

          {shot && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".5px", color: "var(--text-muted)", marginBottom: 4 }}>
                Screenshot attached
              </div>
              <img src={shot} alt="screenshot preview" style={{ width: "100%", border: "1px solid var(--border)", borderRadius: 6 }} />
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button className="btn btn-primary" onClick={send} disabled={busy}>{busy ? "Sending…" : "Send"}</button>
            {msg && <span style={{ fontSize: 12, fontWeight: 700, color: msg.startsWith("✗") ? "var(--red)" : "var(--green)" }}>{msg}</span>}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 8 }}>
            Goes to the Delphi admins with the page you're on and the screenshot above.
          </div>
        </div>
      )}
    </>
  );
}
